import React, { useState, useEffect } from 'react';
import { Modal, Input, List, Spin, Alert, Row, Col, Card, Typography, Space, Upload, Button, InputNumber } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import useFileUpload from '../../api/useFileUpload';

const { Title, Text } = Typography;

const FormLibraryCsvUpload = ({
  mode,
  open,
  onClose,
  initialFormData,
  selectedFile,
  isLoading,
  onSubmit,
  response,
  error,
}) => {
  const initialManualFields = {
    name: '',
    description: '',
    sku_code: '',
    form_type: '',
    tags: '',
    category: '',
    status: '',
    attributes: '',
    target_audience: '',
    logic_type: '',
    sample_report_link: '',
    instructions: '',
    notes: '',
    owned_by: '',
    credit_cost: 0,
    duration: 0,
    thumbnail: '',
    extra_metadata: {
      ai_prompt: '',
      report_configs: {},
      additional_data: {}
    }
  };
  const [manualFields, setManualFields] = useState(initialManualFields);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [apiError, setApiError] = useState(null);
  
  // File upload hook
  const { uploadFile, isLoading: uploadFileLoading } = useFileUpload();

  useEffect(() => {
    if (open && initialFormData) {
      setManualFields({ ...initialManualFields, ...initialFormData });
    } else if (!open) {
      setManualFields(initialManualFields);
      setCsvFile(null);
      setCsvData(null);
      setParseError(null);
      setUploadError(null);
      setApiError(null);
    }
  }, [open, initialFormData]);

  // Handle pre-selected file when modal opens
  useEffect(() => {
    if (open && selectedFile && mode === 'csv') {
      // Define handleCsvSelect inline to avoid use-before-define error
      const handleCsvSelect = (file) => {
        setParsing(true);
        setParseError(null);
        Papa.parse(file, {
          complete: (results) => {
            setParsing(false);
            if (results.errors && results.errors.length > 0) {
              setParseError(results.errors[0].message);
              return;
            }
            setCsvFile(file);
            setCsvData(results.data);
          },
          error: (err) => {
            setParsing(false);
            setParseError(err.message);
          },
        });
        return false;
      };
      handleCsvSelect(selectedFile);
    }
  }, [open, selectedFile, mode]);

  // Helper function to convert error objects to strings
  const formatErrorMessage = (errorObj) => {
    if (typeof errorObj === 'string') return errorObj;
    if (typeof errorObj === 'object' && errorObj !== null) {
      // If it's an array of error objects (like the API response format)
      if (Array.isArray(errorObj)) {
        return errorObj.map(err => {
          if (typeof err === 'string') return err;
          if (err && typeof err === 'object') {
            // Handle validation error objects with {loc, msg, type} structure
            if (err.loc && err.msg) {
              const field = Array.isArray(err.loc) ? err.loc.join('.') : err.loc;
              return `${field}: ${err.msg}`;
            }
            return err.msg || err.message || JSON.stringify(err);
          }
          return String(err);
        }).join(', ');
      }
      // Handle single error object with {loc, msg, type} structure
      if (errorObj.loc && errorObj.msg) {
        const field = Array.isArray(errorObj.loc) ? errorObj.loc.join('.') : errorObj.loc;
        return `${field}: ${errorObj.msg}`;
      }
      // Handle other error object properties
      if (errorObj.msg) return errorObj.msg;
      if (errorObj.message) return errorObj.message;
      if (errorObj.error) return errorObj.error;
      if (errorObj.detail) return errorObj.detail;
      // Try to stringify the object
      try {
        return JSON.stringify(errorObj);
      } catch {
        return 'An error occurred';
      }
    }
    return 'An error occurred';
  };

  // Handle API responses - close modal on success, show error on failure
  useEffect(() => {
    if (error) {
      // Error case - show error in modal
      let errorMsg = mode === 'csv' ? 'Failed to upload CSV.' : 'Failed to add form to library.';
      if (error?.response?.data?.detail) errorMsg = formatErrorMessage(error.response.data.detail);
      else if (error?.response?.data?.message) errorMsg = formatErrorMessage(error.response.data.message);
      else if (error?.response?.data?.errors) errorMsg = formatErrorMessage(error.response.data.errors);
      else if (error?.response?.message) errorMsg = formatErrorMessage(error.response.message);
      else if (error?.message) errorMsg = formatErrorMessage(error.message);
      else if (typeof error === 'string') errorMsg = error;
      else errorMsg = formatErrorMessage(error);
      setApiError(errorMsg);
    } else if (response) {
      // Check if the response indicates success or contains an error
      const hasError = response.error || response.errors || response.status === 'error' || 
                      (response.data && (response.data.error || response.data.errors)) ||
                      response.detail;
      
      if (hasError) {
        // Response contains error information
        let errorMsg = mode === 'csv' ? 'Failed to upload CSV.' : 'Failed to add form to library.';
        if (response.detail) {
          errorMsg = formatErrorMessage(response.detail);
        } else if (response.error) {
          errorMsg = formatErrorMessage(response.error);
        } else if (response.errors) {
          errorMsg = formatErrorMessage(response.errors);
        } else if (response.data?.error) {
          errorMsg = formatErrorMessage(response.data.error);
        } else if (response.data?.errors) {
          errorMsg = formatErrorMessage(response.data.errors);
        }
        setApiError(errorMsg);
      } else if (onClose) {
        // Success case - close modal
        onClose();
      }
    }
  }, [response, error, mode, onClose]);

  const getPreviewInfo = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return {};
    const headers = data[0].map(h => h.toLowerCase());
    let name = '';
    let description = '';
    let questions = [];
    if (headers.includes('name')) {
      name = data[1]?.[headers.indexOf('name')] || '';
    }
    if (headers.includes('description')) {
      description = data[1]?.[headers.indexOf('description')] || '';
    }
    if (headers.includes('question')) {
      questions = data.slice(1).map(row => row[headers.indexOf('question')]).filter(Boolean);
    } else {
      const questionCols = headers
        .map((h, i) => (h !== 'name' && h !== 'description' ? i : null))
        .filter(i => i !== null);
      questions = data.slice(1).map(row => questionCols.map(i => row[i]).join(' | ')).filter(Boolean);
    }
    return {
      name,
      description,
      totalRows: data.length - 1,
      questions,
    };
  };



  const handleManualFieldChange = (field, value) => {
    setManualFields(prev => ({ ...prev, [field]: value }));
  };

  const handleExtraMetadataChange = (field, value) => {
    setManualFields(prev => ({
      ...prev,
      extra_metadata: {
        ...prev.extra_metadata,
        [field]: value
      }
    }));
  };

  const handleThumbnailUpload = async (file) => {
    try {
      const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      const fileFolder = 'form-library-thumbnails';
      
      const uploadResponse = await uploadFile({
        file,
        fileName,
        fileFolder
      });
      
      console.log('Upload response:', uploadResponse); // Debug log
      
      // Check different possible response structures
      const thumbnailUrl = uploadResponse;
      
      console.log('Extracted thumbnail URL:', thumbnailUrl); // Debug log
      
      if (thumbnailUrl) {
        setManualFields(prev => ({
          ...prev,
          thumbnail: thumbnailUrl
        }));
      } else {
        console.error('No thumbnail URL found in response');
      }
    } catch (error) {
      console.error('Thumbnail upload error:', error);
    }
  };

  const handleModalOk = async () => {
    setApiError(null);
    
    if (mode === 'csv') {
      // Compose formData for upload
      const formData = new FormData();
      if (csvFile) {
        formData.append('file', csvFile);
      }
      const meta = {
        name: manualFields.name,
        description: manualFields.description,
        sku_code: manualFields.sku_code,
        form_type: manualFields.form_type,
        tags: manualFields.tags ? manualFields.tags.split(',').map(v => v.trim()).filter(Boolean) : [],
        category: manualFields.category,
        status: manualFields.status,
        attributes: manualFields.attributes ? manualFields.attributes.split(',').map(v => v.trim()).filter(Boolean) : [],
        target_audience: manualFields.target_audience ? manualFields.target_audience.split(',').map(v => v.trim()).filter(Boolean) : [],
        logic_type: manualFields.logic_type,
        sample_report_link: manualFields.sample_report_link,
        instructions: manualFields.instructions,
        notes: manualFields.notes,
        owned_by: manualFields.owned_by,
        credit_cost: parseFloat(manualFields.credit_cost) || 0,
        duration: parseInt(manualFields.duration) || 0,
        thumbnail: manualFields.thumbnail || '',
        extra_metadata: {
          ai_prompt: manualFields.extra_metadata.ai_prompt || '',
          report_configs: manualFields.extra_metadata.report_configs || {},
          additional_data: manualFields.extra_metadata.additional_data || {}
        }
      };
      formData.append('meta', JSON.stringify(meta));
      
      // Debug: Log what's being sent
      console.log('Meta data being sent:', meta);
      
      // Call the parent's onSubmit function with the form data
      if (onSubmit) {
        await onSubmit({ formData, mode: 'csv' });
      }
    } else if (mode === 'add' || mode === 'edit') {
      // Format the data for add/edit mode - convert string fields to arrays
      const formattedData = {
        ...manualFields,
        tags: manualFields.tags ? manualFields.tags.split(',').map(v => v.trim()).filter(Boolean) : [],
        attributes: manualFields.attributes ? manualFields.attributes.split(',').map(v => v.trim()).filter(Boolean) : [],
        target_audience: manualFields.target_audience ? manualFields.target_audience.split(',').map(v => v.trim()).filter(Boolean) : [],
        credit_cost: parseFloat(manualFields.credit_cost) || 0,
        duration: parseInt(manualFields.duration) || 0,
        thumbnail: manualFields.thumbnail,
        extra_metadata: manualFields.extra_metadata
      };
      // Call the parent's onSubmit function with the formatted data
      if (onSubmit) {
        await onSubmit({ data: formattedData, mode });
      }
    }
  };

  const handleModalCancel = () => {
    if (onClose) onClose();
  };

  const preview = csvData ? getPreviewInfo(csvData) : {};

  return (
    <Modal
      title={
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            {mode === 'csv' ? '📊 CSV Upload Preview' : mode === 'edit' ? '✏️ Edit Form' : 'Add Form to Library'}
          </Title>
          <Text type="secondary">
            {mode === 'csv' ? 'Review and configure form details' : mode === 'edit' ? 'Edit the form details and save changes' : 'Fill in the form details to add to library'}
          </Text>
        </div>
      }
      open={open}
      onOk={handleModalOk}
      onCancel={handleModalCancel}
      okButtonProps={{ disabled: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      confirmLoading={isLoading}
      width="90vw"
      style={{ maxWidth: '1200px' }}
      bodyStyle={{
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '24px',
        position: 'relative',
        opacity: isLoading ? 0.5 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
      className="csv-upload-modal"
      centered
      destroyOnClose
      maskClosable={false}
    >
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(255,255,255,0.6)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Spin size="large" />
        </div>
      )}
      {parsing && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Parsing CSV file...</Text>
          </div>
        </div>
      )}
      {parseError && (
        <Alert
          type="error"
          message="CSV Parsing Error"
          description={parseError}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {uploadError && (
        <Alert
          type="error"
          message="Upload Error"
          description={uploadError}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {apiError && (
        <Alert
          type="error"
          message="API Error"
          description={apiError}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Only show Upload button in CSV mode */}
      {/* Always show manual fields */}
      <div style={{ padding: '16px 0' }}>
        <Card
          title={
            <Space>
              <span>📝 Form Details</span>
              <Text type="secondary">Enter or edit form details</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Text strong>Form Name:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Enter form name"
                  value={manualFields.name}
                  onChange={e => handleManualFieldChange('name', e.target.value)}
                  size="large"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Description:</Text>
              <div style={{ marginTop: 4 }}>
                <Input.TextArea
                  placeholder="Enter form description"
                  value={manualFields.description}
                  onChange={e => handleManualFieldChange('description', e.target.value)}
                  rows={3}
                  size="large"
                />
              </div>
            </Col>
          </Row>
        </Card>
        <Card
          title={
            <Space>
              <span>⚙️ Form Configuration</span>
              <Text type="secondary">Additional metadata</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Text strong>SKU Code:</Text>
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input
                  placeholder="Enter SKU code"
                  value={manualFields.sku_code}
                  onChange={e => handleManualFieldChange('sku_code', e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  onClick={() => {
                    // Generate random alphanumeric SKU based on name and description
                    const base = `${manualFields.name || ''}${manualFields.description || ''}`.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                    const random = Math.random().toString(36).substring(2, 7).toUpperCase();
                    const sku = (base.substring(0, 6) + random).replace(/[^A-Z0-9]/g, '');
                    handleManualFieldChange('sku_code', sku);
                  }}
                >
                  Generate
                </Button>
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                Only alphanumeric codes are valid (no spaces or special characters, not words)
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Form Type:</Text>
              <div style={{ marginTop: 4 }}>
                <select
                  value={manualFields.form_type}
                  onChange={e => handleManualFieldChange('form_type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Select form type</option>
                  <option value="survey">Survey</option>
                  <option value="signup">Signup</option>
                  <option value="test">Test</option>
                  <option value="application">Application</option>
                </select>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Category:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Enter category"
                  value={manualFields.category}
                  onChange={e => handleManualFieldChange('category', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Status:</Text>
              <div style={{ marginTop: 4 }}>
                <select
                  value={manualFields.status}
                  onChange={e => handleManualFieldChange('status', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Select status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Logic Type:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Enter logic type"
                  value={manualFields.logic_type}
                  onChange={e => handleManualFieldChange('logic_type', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Owned By:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Enter owner"
                  value={manualFields.owned_by}
                  onChange={e => handleManualFieldChange('owned_by', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Tags:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Comma separated tags (e.g., survey, feedback, assessment)"
                  value={manualFields.tags}
                  onChange={e => handleManualFieldChange('tags', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Attributes:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Comma separated attributes (e.g., required, optional, conditional)"
                  value={manualFields.attributes}
                  onChange={e => handleManualFieldChange('attributes', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Target Audience:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Comma separated audience (e.g., students, teachers, parents)"
                  value={manualFields.target_audience}
                  onChange={e => handleManualFieldChange('target_audience', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Sample Report Link:</Text>
              <div style={{ marginTop: 4 }}>
                <Input
                  placeholder="Enter sample report link"
                  value={manualFields.sample_report_link}
                  onChange={e => handleManualFieldChange('sample_report_link', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24}>
              <Text strong>Instructions:</Text>
              <div style={{ marginTop: 4 }}>
                <Input.TextArea
                  placeholder="Enter detailed instructions for form completion"
                  value={manualFields.instructions}
                  onChange={e => handleManualFieldChange('instructions', e.target.value)}
                  rows={3}
                />
              </div>
            </Col>
            <Col xs={24}>
              <Text strong>Notes:</Text>
              <div style={{ marginTop: 4 }}>
                <Input.TextArea
                  placeholder="Enter additional notes or comments"
                  value={manualFields.notes}
                  onChange={e => handleManualFieldChange('notes', e.target.value)}
                  rows={3}
                />
              </div>
            </Col>
          </Row>
        </Card>
        
        {/* New Fields Card */}
        <Card
          title={
            <Space>
              <span>💰 Pricing & Media</span>
              <Text type="secondary">Credit cost and thumbnail</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Text strong>Credit Cost:</Text>
              <div style={{ marginTop: 4 }}>
                <InputNumber
                  placeholder="Enter credit cost"
                  value={manualFields.credit_cost}
                  onChange={value => handleManualFieldChange('credit_cost', value)}
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Duration (minutes):</Text>
              <div style={{ marginTop: 4 }}>
                <InputNumber
                  placeholder="Enter duration in minutes"
                  value={manualFields.duration}
                  onChange={value => handleManualFieldChange('duration', value)}
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <Text strong>Thumbnail:</Text>
              <div style={{ marginTop: 4 }}>
                <Upload
                  beforeUpload={(file) => {
                    handleThumbnailUpload(file);
                    return false; // Prevent default upload
                  }}
                  accept="image/*"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} loading={uploadFileLoading}>
                    Upload Thumbnail
                  </Button>
                </Upload>
                {manualFields.thumbnail && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="success">✓ Thumbnail uploaded successfully</Text>
                    <div style={{ marginTop: 8 }}>
                      <img 
                        src={manualFields.thumbnail} 
                        alt="Thumbnail preview" 
                        style={{ 
                          maxWidth: '120px', 
                          maxHeight: '80px', 
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9'
                        }} 
                      />
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card>
        
        {/* Extra Metadata Card */}
        <Card
          title={
            <Space>
              <span>🔧 Extra Metadata</span>
              <Text type="secondary">AI prompt and additional configurations</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Text strong>AI Prompt:</Text>
              <div style={{ marginTop: 4 }}>
                <Input.TextArea
                  placeholder="Enter AI prompt for form processing"
                  value={manualFields.extra_metadata.ai_prompt}
                  onChange={e => handleExtraMetadataChange('ai_prompt', e.target.value)}
                  rows={3}
                />
              </div>
            </Col>
            {/* <Col xs={24} sm={12}>
              <Text strong>Report Configs (JSON):</Text>
              <div style={{ marginTop: 4 }}>
                <Input.TextArea
                  placeholder='{"key": "value"}'
                  value={JSON.stringify(manualFields.extra_metadata.report_configs, null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleExtraMetadataChange('report_configs', parsed);
                    } catch (error) {
                      // Keep the string value if JSON is invalid
                      handleExtraMetadataChange('report_configs', e.target.value);
                    }
                  }}
                  rows={4}
                />
              </div>
            </Col> */}
            {/* <Col xs={24} sm={12}>
              <Text strong>Additional Data (JSON):</Text>
              <div style={{ marginTop: 4 }}>
                <Input.TextArea
                  placeholder='{"key": "value"}'
                  value={JSON.stringify(manualFields.extra_metadata.additional_data, null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleExtraMetadataChange('additional_data', parsed);
                    } catch (error) {
                      // Keep the string value if JSON is invalid
                      handleExtraMetadataChange('additional_data', e.target.value);
                    }
                  }}
                  rows={4}
                />
              </div>
            </Col> */}
          </Row>
        </Card>
        {/* CSV Preview and Questions Preview if CSV is uploaded */}
        {mode === 'csv' && csvData && (
          <Card
            title={
              <Space>
                <span>📋 CSV Preview</span>
                <Text type="secondary">({preview.totalRows} rows, {preview.questions?.length || 0} questions)</Text>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {/* Only show CSV preview, do NOT repeat form name/description fields here */}
          </Card>
        )}
        {mode === 'csv' && csvData && preview.questions && preview.questions.length > 0 && (
          <Card
            title={
              <Space>
                <span>❓ Questions Preview</span>
                <Text type="secondary">First few questions from CSV</Text>
              </Space>
            }
          >
            <List
              size="small"
              dataSource={preview.questions.slice(0, 5)}
              renderItem={(question, index) => (
                <List.Item>
                  <Text>
                    <Text strong>{index + 1}.</Text> {question}
                  </Text>
                </List.Item>
              )}
            />
            {preview.questions.length > 5 && (
              <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                ... and {preview.questions.length - 5} more questions
              </Text>
            )}
          </Card>
        )}
      </div>
    </Modal>
  );
};

FormLibraryCsvUpload.propTypes = {
  mode: PropTypes.oneOf(['csv', 'add', 'edit']),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  initialFormData: PropTypes.object,
  selectedFile: PropTypes.object,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  response: PropTypes.object,
  error: PropTypes.object,
};

export default FormLibraryCsvUpload;